import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import { formatApiError } from '@/lib/utils';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import {
  Plus, Search, Briefcase, X, Loader2, Edit3, Trash2,
  ChevronRight, Scale, Activity, Receipt, FolderLock,
  User, AlertCircle, FileText, Upload, CheckCircle2
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

const FUEROS = ['Civil', 'Penal', 'Laboral', 'Familia', 'Comercial', 'Contencioso Administrativo', 'Constitucional', 'Sucesiones', 'Concursos y Quiebras', 'Previsional', 'Previsional Social', 'Administrativo'];

const ESTADOS = {
  inicio:      { cls: 'text-blue-300 bg-blue-500/10 border-blue-500/20', label: 'Inicio' },
  en_proceso:  { cls: 'text-[#C9A44C] bg-[#C9A44C]/10 border-[#C9A44C]/20', label: 'En proceso' },
  mediacion:   { cls: 'text-purple-300 bg-purple-500/10 border-purple-500/20', label: 'Mediación' },
  sentencia:   { cls: 'text-green-300 bg-green-500/10 border-green-500/20', label: 'Sentencia' },
  archivado:   { cls: 'text-[#888] bg-white/5 border-white/10', label: 'Archivado' },
  en_tramite:  { cls: 'text-[#C9A44C] bg-[#C9A44C]/10 border-[#C9A44C]/20', label: 'En trámite' },
};

const badge = (e) => ESTADOS[e] || { cls: 'text-[#888] bg-white/5 border-white/10', label: e };
const fmt = (n) => new Intl.NumberFormat('es-AR').format(n || 0);
const fmtM = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtF = (s) => { try { return new Date(s).toLocaleDateString('es-AR'); } catch { return s || '—'; } };

// Convierte el `detail` de un error de API a string. Un 422 de FastAPI devuelve
// un ARRAY de objetos; renderizarlo directo crashea React ("Objects are not valid
// as a React child"). Siempre devolver string.


/* ─── Modal crear expediente ─────────────────────────────────── */
function ModalCrear({ onClose, onCreated }) {
  const [clientes, setClientes] = useState([]);
  const [f, setF] = useState({
    cliente_id: '', caratula: '', numero_expediente: '',
    fuero: '', juzgado: '', secretaria: '', estado_procesal: 'inicio',
    prioridad: 'normal', actor: '', demandado: '', notas: '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/api/clientes/').then((r) => setClientes(r.data)).catch(() => {});
  }, []);

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      const body = { ...f };
      await api.post('/api/expedientes/', body);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al crear expediente'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg my-4 bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Nuevo expediente</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <select className={IC} value={f.cliente_id} onChange={set('cliente_id')} required>
            <option value="" className="bg-[#141414]">Seleccionar cliente *</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id} className="bg-[#141414]">
                {c.apellido ? `${c.apellido.toUpperCase()}, ${c.nombre}` : c.nombre}
              </option>
            ))}
          </select>
          <input className={IC} placeholder="Carátula *" value={f.caratula} onChange={set('caratula')} required />
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="N° Expediente" value={f.numero_expediente} onChange={set('numero_expediente')} />
            <select className={IC} value={f.fuero} onChange={set('fuero')}>
              <option value="" className="bg-[#141414]">Fuero</option>
              {FUEROS.map((fu) => <option key={fu} value={fu} className="bg-[#141414]">{fu}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Juzgado" value={f.juzgado} onChange={set('juzgado')} />
            <input className={IC} placeholder="Secretaría" value={f.secretaria} onChange={set('secretaria')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Actor" value={f.actor} onChange={set('actor')} />
            <input className={IC} placeholder="Demandado" value={f.demandado} onChange={set('demandado')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className={IC} value={f.estado_procesal} onChange={set('estado_procesal')}>
              {Object.entries(ESTADOS).map(([k, v]) => (
                <option key={k} value={k} className="bg-[#141414]">{v.label}</option>
              ))}
            </select>
            <select className={IC} value={f.prioridad} onChange={set('prioridad')}>
              {['urgente', 'alta', 'normal', 'media', 'baja'].map((p) => (
                <option key={p} value={p} className="bg-[#141414]">{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
          <textarea className={IC + ' resize-none'} rows={2} placeholder="Notas" value={f.notas} onChange={set('notas')} />
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear expediente'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── VaultTab: subir y listar evidencias ───────────────────── */
function VaultTab({ expId, evidencias, onUploaded }) {
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState('');
  const [error, setError] = useState('');
  const [viendoArchivo, setViendoArchivo] = useState(null);
  const [cargandoVisor, setCargandoVisor] = useState(false);

  const TIPOS_OK = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.txt,.xlsx,.xls,.html';

  const cerrarVisor = () => {
    if (viendoArchivo?.blobUrl) URL.revokeObjectURL(viendoArchivo.blobUrl);
    setViendoArchivo(null);
  };

  const manejarArchivo = async (file) => {
    if (!file) return;
    setError(''); setSubiendo(true);
    setProgreso(`Subiendo ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/vault/evidencias?expediente_id=${expId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProgreso('');
      onUploaded();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, 'Error al subir el archivo'));
      setProgreso('');
    } finally { setSubiendo(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) manejarArchivo(file);
  };

  const esPDF = (mime) => mime === 'application/pdf';
  const esImagen = (mime) => mime?.startsWith('image/');
  const esTexto = (mime) => mime === 'text/plain';

  const abrirArchivo = async (ev) => {
    setCargandoVisor(true);
    setError('');
    try {
      const r = await api.get(`/api/vault/evidencias/${ev.id}/stream`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      if (esPDF(ev.tipo_mimetype) || esImagen(ev.tipo_mimetype) || esTexto(ev.tipo_mimetype)) {
        setViendoArchivo({ ...ev, blobUrl });
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = ev.nombre || 'archivo';
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
      }
    } catch {
      setError('No se pudo cargar el archivo');
    } finally {
      setCargandoVisor(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Visor inline */}
      {cargandoVisor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-[#C9A44C]" />
            <span className="text-sm text-[#888]">Cargando archivo…</span>
          </div>
        </div>
      )}
      {viendoArchivo && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={cerrarVisor}>
          <div className="w-full max-w-4xl bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <span className="text-sm font-medium truncate">{viendoArchivo.nombre}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => window.open(viendoArchivo.blobUrl, '_blank')}
                  className="text-[10px] px-3 py-1 border border-[rgba(201,164,76,0.25)] text-[#C9A44C] rounded-lg hover:bg-[#C9A44C]/10 transition-all">
                  Abrir en nueva pestaña
                </button>
                <button onClick={cerrarVisor} className="text-[#888] hover:text-white ml-1"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#070707]" style={{ minHeight: '400px' }}>
              {esPDF(viendoArchivo.tipo_mimetype) && (
                <iframe src={viendoArchivo.blobUrl} title={viendoArchivo.nombre}
                  className="w-full h-full" style={{ minHeight: '70vh', border: 'none' }} />
              )}
              {esImagen(viendoArchivo.tipo_mimetype) && (
                <img src={viendoArchivo.blobUrl} alt={viendoArchivo.nombre}
                  className="max-w-full max-h-full mx-auto object-contain p-4" style={{ maxHeight: '80vh' }} />
              )}
              {esTexto(viendoArchivo.tipo_mimetype) && (
                <iframe src={viendoArchivo.blobUrl} title={viendoArchivo.nombre}
                  className="w-full h-full" style={{ minHeight: '70vh', border: 'none', background: '#111', color: '#eee' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Zona de drop */}
      <label
        className={`block border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
          subiendo
            ? 'border-[#C9A44C]/50 bg-[#C9A44C]/5'
            : 'border-white/10 hover:border-[#C9A44C]/40 hover:bg-white/[0.02]'
        }`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}>
        <input type="file" accept={TIPOS_OK} className="hidden"
          onChange={(e) => manejarArchivo(e.target.files[0])} disabled={subiendo} />
        {subiendo ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" />
            <span className="text-xs text-[#C9A44C]">{progreso}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <Upload className="w-6 h-6 text-[#888]" />
            <span className="text-xs text-[#888]">Clic para subir o arrastrá un archivo</span>
            <span className="text-[10px] text-[#666]">PDF · DOC · XLSX · JPG · PNG · TXT (máx 49 MB)</span>
          </div>
        )}
      </label>

      {error && (
        <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 text-red-400 text-xs">{error}</div>
      )}

      {/* Lista de archivos */}
      {(!evidencias || evidencias.length === 0) ? (
        <p className="text-xs text-[#555] py-4 text-center">Sin archivos en la bóveda</p>
      ) : (
        <div className="space-y-1.5">
          {evidencias.map((ev) => (
            <button key={ev.id} onClick={() => abrirArchivo(ev)}
              className="w-full flex items-center gap-3 p-2.5 bg-white/[0.02] border border-white/5 rounded-lg hover:border-[#C9A44C]/25 transition-all text-left">
              <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{ev.nombre}</div>
                <div className="text-[10px] text-[#888]">{ev.tipo_mimetype} · {fmtF(ev.fecha_creacion)}</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-[#888]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Vista 360 (panel lateral) ─────────────────────────────── */
const TABS_360 = [
  { id: 'datos', label: 'Datos', icon: Scale },
  { id: 'movimientos', label: 'Movimientos', icon: Activity },
  { id: 'honorarios', label: 'Honorarios', icon: Receipt },
  { id: 'evidencias', label: 'Bóveda', icon: FolderLock },
  { id: 'colaboradores', label: 'Equipo', icon: User, teamOnly: true },
];

function Vista360({ expId, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('datos');
  const [nuevoMov, setNuevoMov] = useState({ tipo: 'nota', descripcion: '', titulo: '' });
  const [savingMov, setSavingMov] = useState(false);
  const [planActual, setPlanActual] = useState(null);

  useEffect(() => {
    api.get('/api/suscripcion/estado').then((r) => setPlanActual(r.data?.plan || null)).catch(() => {});
  }, []);

  const tabsVisibles = TABS_360.filter((t) => !(t.teamOnly && planActual === 'independiente'));

  const cargar = useCallback(() => {
    setLoading(true);
    api.get(`/api/expedientes/${expId}/360`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [expId]);

  useEffect(() => { cargar(); }, [cargar]);

  const agregarMovimiento = async () => {
    if (!nuevoMov.descripcion.trim()) return;
    setSavingMov(true);
    try {
      await api.post(`/api/expedientes/${expId}/movimientos`, nuevoMov);
      setNuevoMov({ tipo: 'nota', descripcion: '', titulo: '' });
      cargar();
    } finally { setSavingMov(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[rgba(201,164,76,0.2)] rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
      {/* Header */}
      <div className="flex items-start justify-between p-5 border-b border-[rgba(201,164,76,0.12)]">
        <div className="min-w-0 flex-1 pr-3">
          <div className="text-[10px] text-[#C9A44C] uppercase tracking-[0.2em] mb-0.5">Vista 360°</div>
          <h3 className="text-base font-light truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
            {loading ? '...' : (data?.expediente?.caratula || 'Expediente')}
          </h3>
          {data?.expediente && (
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[9px] px-2 py-0.5 rounded-full border ${badge(data.expediente.estado_procesal).cls}`}>
                {badge(data.expediente.estado_procesal).label}
              </span>
              {data.expediente.numero_expediente && (
                <span className="text-[10px] text-[#888]">Exp. {data.expediente.numero_expediente}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onEdit && data && (
            <button onClick={() => onEdit(data.expediente)} className="text-[#888] hover:text-[#C9A44C]">
              <Edit3 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[rgba(201,164,76,0.08)] overflow-x-auto">
        {tabsVisibles.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[10px] uppercase tracking-wide whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${
              tab === id ? 'border-[#C9A44C] text-[#C9A44C]' : 'border-transparent text-[#888] hover:text-[#ccc]'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-y-auto p-5 min-h-0">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#C9A44C]" /></div>
        ) : !data ? (
          <p className="text-sm text-[#555]">Error al cargar</p>
        ) : (
          <>
            {/* Tab Datos */}
            {tab === 'datos' && (
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2">Cliente</p>
                  <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3 text-sm">
                    <div>{data.cliente?.nombre} {data.cliente?.apellido}</div>
                    {data.cliente?.dni_cuit && <div className="text-[#888] text-xs">DNI/CUIT: {data.cliente.dni_cuit}</div>}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2">Detalles del expediente</p>
                  <div className="space-y-1.5 text-sm">
                    {[
                      ['Fuero', data.expediente?.fuero],
                      ['Juzgado', data.expediente?.juzgado],
                      ['Secretaría', data.expediente?.secretaria],
                      ['Actor', data.expediente?.actor],
                      ['Demandado', data.expediente?.demandado],
                      ['Prioridad', data.expediente?.prioridad],
                      ['Inicio', data.expediente?.fecha_inicio ? fmtF(data.expediente.fecha_inicio) : null],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="text-[#888] w-24 flex-shrink-0 text-xs">{k}</span>
                        <span className="text-xs">{v}</span>
                      </div>
                    ))}
                    {data.expediente?.notas && (
                      <div className="pt-1 border-t border-white/5">
                        <p className="text-[10px] text-[#888] mb-1">Notas</p>
                        <p className="text-xs text-[#ccc]">{data.expediente.notas}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tab Movimientos */}
            {tab === 'movimientos' && (
              <div className="space-y-3">
                {/* Agregar movimiento */}
                <div className="bg-white/[0.02] border border-[rgba(201,164,76,0.1)] rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wide text-[#C9A44C] mb-2">Registrar movimiento</p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <select className={IC + ' text-xs py-2'}
                      value={nuevoMov.tipo}
                      onChange={(e) => setNuevoMov((p) => ({ ...p, tipo: e.target.value }))}>
                      {['nota', 'audiencia', 'escrito', 'notificacion', 'resolucion', 'pericia', 'mediacion', 'otro'].map((t) => (
                        <option key={t} value={t} className="bg-[#141414]">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                    <input className={IC + ' text-xs py-2'} placeholder="Título (opcional)"
                      value={nuevoMov.titulo}
                      onChange={(e) => setNuevoMov((p) => ({ ...p, titulo: e.target.value }))} />
                  </div>
                  <textarea className={IC + ' resize-none text-xs py-2 mb-2'} rows={2}
                    placeholder="Descripción *"
                    value={nuevoMov.descripcion}
                    onChange={(e) => setNuevoMov((p) => ({ ...p, descripcion: e.target.value }))} />
                  <button onClick={agregarMovimiento} disabled={savingMov || !nuevoMov.descripcion.trim()}
                    className="w-full text-[#070707] text-xs font-semibold py-2 rounded-lg disabled:opacity-40 flex items-center justify-center gap-1"
                    style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                    {savingMov ? <Loader2 className="w-3 h-3 animate-spin" /> : '+ Registrar'}
                  </button>
                </div>
                {/* Lista movimientos */}
                {(!data.movimientos || data.movimientos.length === 0) ? (
                  <p className="text-xs text-[#555] py-4 text-center">Sin movimientos registrados</p>
                ) : (
                  <div className="relative pl-4 border-l border-[rgba(201,164,76,0.15)] space-y-3">
                    {data.movimientos.map((m) => (
                      <div key={m.id} className="relative">
                        <div className="absolute -left-[17px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#C9A44C]/80 border-2 border-[#0A0A0A]" />
                        <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] uppercase tracking-wide text-[#C9A44C]">{m.tipo}</span>
                            <span className="text-[10px] text-[#666]">{fmtF(m.fecha)}</span>
                          </div>
                          {m.titulo && <div className="text-xs font-medium mb-0.5">{m.titulo}</div>}
                          <div className="text-xs text-[#ccc]">{m.descripcion}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab Honorarios */}
            {tab === 'honorarios' && (
              <div className="space-y-2">
                {(!data.honorarios || data.honorarios.length === 0) ? (
                  <p className="text-xs text-[#555] py-8 text-center">Sin honorarios registrados</p>
                ) : (
                  data.honorarios.map((h) => (
                    <div key={h.id} className="bg-white/[0.02] border border-white/5 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs font-medium">{h.tipo}</span>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full border ${
                          h.estado === 'cobrado' ? 'text-green-300 bg-green-500/10 border-green-500/20'
                          : h.estado === 'vencido' ? 'text-red-300 bg-red-500/10 border-red-500/20'
                          : 'text-[#C9A44C] bg-[#C9A44C]/10 border-[#C9A44C]/20'
                        }`}>{h.estado}</span>
                      </div>
                      {h.descripcion && <div className="text-xs text-[#888] mb-1">{h.descripcion}</div>}
                      <div className="grid grid-cols-3 gap-2 text-[10px] mt-2">
                        <div className="min-w-0"><div className="text-[#888]">Total</div><div className="truncate">{fmtM(h.monto)}</div></div>
                        <div className="min-w-0"><div className="text-[#888]">Pagado</div><div className="text-green-300 truncate">{fmtM(h.monto_pagado)}</div></div>
                        <div className="min-w-0"><div className="text-[#888]">Saldo</div><div className="text-red-300 truncate">{fmtM((h.monto || 0) - (h.monto_pagado || 0))}</div></div>
                      </div>
                      {h.cuotas_total > 0 && (
                        <div className="text-[10px] text-[#888] mt-1">{h.cuotas_total} cuotas de {fmtM(h.monto_cuota)}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab Bóveda */}
            {tab === 'evidencias' && (
              <VaultTab expId={expId} evidencias={data.evidencias} onUploaded={cargar} />
            )}

            {/* Tab Colaboradores */}
            {tab === 'colaboradores' && (
              <div className="space-y-2">
                {(!data.colaboradores || data.colaboradores.length === 0) ? (
                  <p className="text-xs text-[#555] py-8 text-center">Sin colaboradores asignados</p>
                ) : (
                  data.colaboradores.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[#C9A44C]/12 border border-[#C9A44C]/25 flex items-center justify-center text-[#C9A44C] text-xs flex-shrink-0">
                        {(c.nombre || '?').charAt(0)}
                      </div>
                      <div>
                        <div className="text-xs font-medium">{c.nombre} {c.apellido}</div>
                        <div className="text-[10px] text-[#888]">{c.rol}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </div>
  );
}

/* ─── Modal editar expediente ─────────────────────────────────── */
function ModalEditar({ expediente, onClose, onSaved }) {
  const [f, setF] = useState({
    caratula: expediente.caratula || '',
    numero_expediente: expediente.numero_expediente || '',
    fuero: expediente.fuero || '',
    juzgado: expediente.juzgado || '',
    secretaria: expediente.secretaria || '',
    estado_procesal: expediente.estado_procesal || 'inicio',
    prioridad: expediente.prioridad || 'normal',
    actor: expediente.actor || '',
    demandado: expediente.demandado || '',
    notas: expediente.notas || '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      const body = { ...f };
      await api.put(`/api/expedientes/${expediente.id}`, body);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg my-4 bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Editar expediente</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <input className={IC} placeholder="Carátula *" value={f.caratula} onChange={set('caratula')} required />
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="N° Expediente" value={f.numero_expediente} onChange={set('numero_expediente')} />
            <select className={IC} value={f.fuero} onChange={set('fuero')}>
              <option value="" className="bg-[#141414]">Fuero</option>
              {FUEROS.map((fu) => <option key={fu} value={fu} className="bg-[#141414]">{fu}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Juzgado" value={f.juzgado} onChange={set('juzgado')} />
            <input className={IC} placeholder="Secretaría" value={f.secretaria} onChange={set('secretaria')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Actor" value={f.actor} onChange={set('actor')} />
            <input className={IC} placeholder="Demandado" value={f.demandado} onChange={set('demandado')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className={IC} value={f.estado_procesal} onChange={set('estado_procesal')}>
              {Object.entries(ESTADOS).map(([k, v]) => (
                <option key={k} value={k} className="bg-[#141414]">{v.label}</option>
              ))}
            </select>
            <select className={IC} value={f.prioridad} onChange={set('prioridad')}>
              {['urgente', 'alta', 'normal', 'media', 'baja'].map((p) => (
                <option key={p} value={p} className="bg-[#141414]">{p}</option>
              ))}
            </select>
          </div>
          <textarea className={IC + ' resize-none'} rows={2} placeholder="Notas" value={f.notas} onChange={set('notas')} />
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Página principal ─────────────────────────────────────────── */
export default function Expedientes() {
  const [expedientes, setExpedientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [showCrear, setShowCrear] = useState(false);
  const [vista360Id, setVista360Id] = useState(null);
  const [editando, setEditando] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([
        api.get('/api/expedientes/'),
        api.get('/api/clientes/'),
      ]);
      setExpedientes(e.data);
      setClientes(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const eliminar = async (id, caratula) => {
    if (!(await showConfirm(`¿Eliminar "${caratula}"? Esta acción es irreversible.`, { titulo: 'Eliminar expediente' }))) return;
    try {
      await api.delete(`/api/expedientes/${id}`);
      cargar();
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtrados = expedientes.filter((e) => {
    const t = `${e.caratula || ''} ${e.numero_expediente || ''} ${e.cliente_nombre || ''} ${e.fuero || ''}`.toLowerCase();
    const ok_q = q ? t.includes(q.toLowerCase()) : true;
    const ok_e = filtroEstado ? e.estado_procesal === filtroEstado : true;
    return ok_q && ok_e;
  });

  const prioridadColor = (p) => {
    if (p === 'urgente') return 'text-red-400';
    if (p === 'alta') return 'text-orange-400';
    return 'text-[#888]';
  };

  return (
    <DashboardLayout title="Expedientes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <input className={IC + ' pl-10'} placeholder="Buscar por carátula, número, cliente..."
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className={IC + ' sm:w-44'} value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="" className="bg-[#141414]">Todos los estados</option>
          {Object.entries(ESTADOS).map(([k, v]) => (
            <option key={k} value={k} className="bg-[#141414]">{v.label}</option>
          ))}
        </select>
        <button onClick={() => setShowCrear(true)}
          className="text-[#070707] font-semibold px-5 py-2.5 rounded-lg flex items-center justify-center gap-2 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
          <Plus className="w-4 h-4" /> Nuevo expediente
        </button>
      </div>

      <p className="text-xs text-[#666] mb-3">
        {filtrados.length} expediente{filtrados.length !== 1 ? 's' : ''}
      </p>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-20 text-[#666]">
          <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{expedientes.length === 0 ? 'Aún no hay expedientes' : 'Sin resultados'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtrados.map((e) => {
            const b = badge(e.estado_procesal);
            return (
              <div key={e.id}
                className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4 hover:border-[#C9A44C]/25 transition-all cursor-pointer"
                onClick={() => setVista360Id(e.id)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-[#C9A44C]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Scale className="w-4 h-4 text-[#C9A44C]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{e.caratula}</div>
                      <div className="text-[11px] text-[#888] mt-0.5 flex flex-wrap gap-x-2">
                        {e.cliente_nombre && <span>{e.cliente_nombre}</span>}
                        {e.numero_expediente && <span>· Exp. {e.numero_expediente}</span>}
                        {e.fuero && <span>· {e.fuero}</span>}
                        {e.juzgado && <span>· {e.juzgado}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[9px] uppercase px-2.5 py-1 rounded-full border hidden sm:inline ${b.cls}`}>
                      {b.label}
                    </span>
                    {e.prioridad && e.prioridad !== 'normal' && (
                      <AlertCircle className={`w-3.5 h-3.5 hidden sm:block ${prioridadColor(e.prioridad)}`} />
                    )}
                    <button onClick={(ev) => { ev.stopPropagation(); setVista360Id(e.id); }}
                      className="text-[#888] hover:text-[#C9A44C] transition-colors">
                      <FileText className="w-4 h-4" />
                    </button>
                    <button onClick={(ev) => { ev.stopPropagation(); setEditando(e); }}
                      className="text-[#888] hover:text-[#C9A44C] transition-colors">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={(ev) => { ev.stopPropagation(); eliminar(e.id, e.caratula); }}
                      className="text-[#888] hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vista 360 */}
      {vista360Id && (
        <Vista360
          expId={vista360Id}
          onClose={() => setVista360Id(null)}
          onEdit={(exp) => { setVista360Id(null); setEditando(exp); }}
        />
      )}

      {showCrear && (
        <ModalCrear onClose={() => setShowCrear(false)} onCreated={() => { setShowCrear(false); cargar(); }} />
      )}
      {editando && (
        <ModalEditar expediente={editando} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />
      )}
    </DashboardLayout>
  );
}
